import BenchlingConfig from 'BenchlingConfig';

import {
  AdvancedAnalysisFeature,
  AssaysFeature,
  CapabilityFeature,
  DeveloperPlatformFeature,
  InsightsFeature,
  InventoryFeature,
  LabAutomationFeature,
  MolbioFeature,
  ProcessesFeature,
  RecipesFeature,
  RegistryFeature,
  ResultsFeature,
  V2RequestsFeature,
} from './CapabilityFeatureConstants';

// This should exactly match the previous functionality of BenchlingConfig.ENABLE_X gating features directly
// in components.
export default function isCapabilityFeatureEnabledFromBenchlingConfig(feature: CapabilityFeature): boolean {
  // Define the map in this function so tests can control feature enablement with legacy flags
  const featureToLegacyIsEnabled: {[key in CapabilityFeature]: boolean} = {
    [AdvancedAnalysisFeature.ADVANCED_ANALYSIS_CREATE_STATISTICS]:
      BenchlingConfig.ENABLE_ANALYSIS_TOOL_REGRESSIONS,
    [AdvancedAnalysisFeature.ADVANCED_ANALYSIS_CREATE_TEMPLATES]:
      BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES,
    [InsightsFeature.INSIGHTS_CREATE]: BenchlingConfig.ENABLE_EXTERNAL_ANALYTICS_UI,
    [InsightsFeature.INSIGHTS_DISCOVER]: BenchlingConfig.ENABLE_EXTERNAL_ANALYTICS_UI,
    [InsightsFeature.INSIGHTS_DELETE]: true,
    [InsightsFeature.INSIGHTS_EDIT]: true,
    [InsightsFeature.INSIGHTS_MOVE]: true,
    [InsightsFeature.INSIGHTS_OPEN]: true,
    [InventoryFeature.INVENTORY_ADMIN]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_DISCOVER]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_IMPORT]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_EXPORT]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_CREATE]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_DELETE]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_EDIT]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_MOVE]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_OPEN]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_SHOW_DETAILS]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_AT_MENTION]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_ARCHIVE]: BenchlingConfig.ENABLE_STORAGE,
    [InventoryFeature.INVENTORY_UNARCHIVE]: BenchlingConfig.ENABLE_STORAGE,
    [LabAutomationFeature.LAB_AUTOMATION_ADMIN]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_ARCHIVE]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_CREATE]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_DELETE]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_DISCOVER]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_EDIT]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_EDIT_TAGS]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_IMPORT]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_OPEN]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [LabAutomationFeature.LAB_AUTOMATION_UNARCHIVE]:
      BenchlingConfig.ENABLE_RUNS ||
      BenchlingConfig.ENABLE_LAB_AUTOMATION ||
      BenchlingConfig.INSTRUMENTS_ENABLE,
    [MolbioFeature.MOLBIO_ADMIN]: true,
    [MolbioFeature.MOLBIO_IMPORT]: true,
    [MolbioFeature.MOLBIO_EXPORT]: true,
    [MolbioFeature.MOLBIO_CREATE]: true,
    [MolbioFeature.MOLBIO_DELETE]: true,
    [MolbioFeature.MOLBIO_EDIT]: true,
    [MolbioFeature.MOLBIO_MOVE]: true,
    [MolbioFeature.MOLBIO_OPEN]: true,
    [MolbioFeature.MOLBIO_ARCHIVE]: true,
    [MolbioFeature.MOLBIO_UNARCHIVE]: true,
    [MolbioFeature.MOLBIO_ADD_TO_WORKLIST]: true,
    [ProcessesFeature.PROCESSES_ADMIN]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_AT_MENTION]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_ARCHIVE]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_CREATE]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_DELETE]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_DISCOVER]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_EDIT]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_EDIT_TAGS]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_EXPORT]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_OPEN]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_MOVE]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_SHOW_DETAILS]: BenchlingConfig.WORKFLOWS_ENABLE,
    [ProcessesFeature.PROCESSES_UNARCHIVE]: BenchlingConfig.WORKFLOWS_ENABLE,
    [RegistryFeature.REGISTRY_EXPORT]: true,
    [RegistryFeature.REGISTRY_MOVE]: true,
    [RegistryFeature.REGISTRY_OPEN]: true,
    [RegistryFeature.REGISTRY_UNREGISTER]: true,
    [RegistryFeature.REGISTRY_WORKLISTS]: true,
    [RegistryFeature.REGISTRY_ADMIN]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_DISCOVER]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_IMPORT]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_CREATE]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_DELETE]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_EDIT]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_EDIT_TAGS]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_REGISTER]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_SET_SCHEMA]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_SHOW_DETAILS]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_AT_MENTION]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_ARCHIVE]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_UNARCHIVE]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_BATCHES]: BenchlingConfig.ENABLE_REGISTRY,
    [RegistryFeature.REGISTRY_MIXTURES]: BenchlingConfig.ENABLE_REGISTRY,
    [ResultsFeature.RESULTS_ADMIN]: BenchlingConfig.ENABLE_NEW_RESULTS,
    [ResultsFeature.RESULTS_CREATE]: BenchlingConfig.ENABLE_NEW_RESULTS,
    [ResultsFeature.RESULTS_OPEN]: BenchlingConfig.ENABLE_NEW_RESULTS,
    [ResultsFeature.RESULTS_SHOW_DETAILS]: BenchlingConfig.ENABLE_NEW_RESULTS,
    [DeveloperPlatformFeature.DEVELOPER_PLATFORM_DISCOVER]: true,
    [DeveloperPlatformFeature.DEVELOPER_PLATFORM_OPEN]: true,
    [DeveloperPlatformFeature.DEVELOPER_PLATFORM_ADMIN]: true,
    [DeveloperPlatformFeature.DEVELOPER_PLATFORM_API_KEY_AUTH]: true,
    [RecipesFeature.RECIPES_ADMIN]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_ARCHIVE]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_AT_MENTION]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_CREATE_ANALYSIS]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_CREATE_RECIPE]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_CREATE_STUDY]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_DISCOVER]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_EDIT]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_EDIT_CONFIG]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_EXECUTE_EXPERIMENT]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_EXPORT]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_MOVE]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_OPEN]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_PLAN_EXPERIMENT]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_SHOW_DETAILS]: BenchlingConfig.PROCEDURES_ENABLE,
    [RecipesFeature.RECIPES_UNARCHIVE]: BenchlingConfig.PROCEDURES_ENABLE,
    [AssaysFeature.ASSAYS_ADMIN]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_ARCHIVE]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_AT_MENTION]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_CREATE_ANALYSIS]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_CREATE_ASSAY]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_CREATE_STUDY]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_DISCOVER]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_EDIT_CONFIG]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_EXECUTE_EXPERIMENT]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_EXPORT]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_MOVE]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_OPEN]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_PLAN_EXPERIMENT]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_SHOW_DETAILS]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [AssaysFeature.ASSAYS_UNARCHIVE]: BenchlingConfig.PROCEDURES_ENABLE_ASSAYS,
    [V2RequestsFeature.V2_REQUESTS_ADMIN]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_CREATE]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_DISCOVER]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_EDIT]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_DELETE]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_MOVE]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_OPEN]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_SHOW_DETAILS]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_ARCHIVE]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_UNARCHIVE]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_AT_MENTION]: BenchlingConfig.REQUESTS_V2_ENABLE,
    [V2RequestsFeature.V2_REQUESTS_EXPORT]: BenchlingConfig.REQUESTS_V2_ENABLE,
  };
  return featureToLegacyIsEnabled[feature];
}
